import { useCallback, useEffect, useMemo, useRef, useState, type RefCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useUI } from '@/hooks/ui/useUI';
import { useDebounce } from '@/hooks/ui/useDebounce';
import { usePagePermissions } from '@/hooks/authorization/usePagePermissions';
import { securityGroupingListService } from '@/api/domains/securityGrouping/services/securityGroupingList.service';
import { groupAdminsAvailableService } from '@/api/domains/securityGrouping/services/groupAdminsAvailable.service';
import { groupAdminsAssignedService } from '@/api/domains/securityGrouping/services/groupAdminsAssigned.service';
import { groupAdminsSaveService } from '@/api/domains/securityGrouping/services/groupAdminsSave.service';
import type { GroupAdminItem, LC_SaveGroupAdminsIn } from '@/api/domains/securityGrouping';


const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 400;
const CANCEL_GUARD_DELAY_MS = SEARCH_DEBOUNCE_MS + 100;

// ── Return type ───────────────────────────────────────────────────────────────

export interface UseAssignAdministratorsTabResult {
  groupingOptions: { value: string; label: string }[];
  selectedGroupId: number | null;
  onGroupChange: (groupId: number | null) => void;

  available: GroupAdminItem[];
  filteredAvailable: GroupAdminItem[];
  isAvailableLoading: boolean;
  hasMoreAvailable: boolean;
  totalAvailable: number;
  availableSearchText: string;
  onAvailableSearch: (text: string) => void;
  availableSentinelRef: RefCallback<HTMLDivElement>;
  availableScrollContainerRef: RefCallback<HTMLDivElement>;

  assigned: GroupAdminItem[];
  filteredAssigned: GroupAdminItem[];
  isAssignedLoading: boolean;
  hasMoreAssigned: boolean;
  totalAssigned: number;
  assignedSearchText: string;
  onAssignedSearch: (text: string) => void;
  assignedSentinelRef: RefCallback<HTMLDivElement>;
  assignedScrollContainerRef: RefCallback<HTMLDivElement>;

  selectedAvailableIds: Set<number>;
  onToggleAvailable: (employeeId: number) => void;
  onSelectAllAvailable: () => void;
  isAllAvailableSelected: boolean;
  isAvailableIndeterminate: boolean;

  selectedAssignedIds: Set<number>;
  onToggleAssigned: (employeeId: number) => void;
  onSelectAllAssigned: () => void;
  isAllAssignedSelected: boolean;
  isAssignedIndeterminate: boolean;

  onAdd: () => void;
  onRemove: () => void;
  onDoubleClickAvailable: (employee: GroupAdminItem) => void;
  onDoubleClickAssigned: (employee: GroupAdminItem) => void;
  onAddAllFromServer: () => void;
  onRemoveAllFromServer: () => void;
  isAddAllPending: boolean;
  isRemoveAllPending: boolean;

  isDirty: boolean;
  isSaving: boolean;
  canEdit: boolean;
  onSave: () => Promise<void>;
  onCancel: () => void;
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useAssignAdministratorsTab(): UseAssignAdministratorsTabResult {
  const { t: translate } = useTranslation();
  const { showNotification, showApiError } = useUI();
  const { canEdit } = usePagePermissions();

  // ── Grouping selector ─────────────────────────────────────────────────
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  const [groupingOptions, setGroupingOptions] = useState<{ value: string; label: string }[]>([]);

  const loadGroupingOptions = useCallback(async () => {
    const response = await securityGroupingListService.list({ status: 'enabled', pageSize: 1000 });
    if (response.success && response.data) {
      setGroupingOptions(
        response.data.items.map((group) => ({ value: String(group.id), label: group.name })),
      );
    } else {
      showApiError(response, translate('securityGrouping:assignAdministrators.messages.loadError'));
    }
  }, [showApiError, translate]);

  useEffect(() => { void loadGroupingOptions(); }, [loadGroupingOptions]);

  // ── Available panel — infinite scroll ────────────────────────────────
  const [available, setAvailable] = useState<GroupAdminItem[]>([]);
  const [isAvailableLoading, setIsAvailableLoading] = useState(false);
  const [hasMoreAvailable, setHasMoreAvailable] = useState(true);
  const [totalAvailable, setTotalAvailable] = useState(0);
  const availablePageRef = useRef(1);
  const availableAccumulatedRef = useRef(0);
  const latestAvailableRequestRef = useRef(0);
  const fetchAvailableRef = useRef<() => Promise<void>>(async () => {});
  const isFetchingAvailableRef = useRef(false);
  const [availableSentinelEl, setAvailableSentinelEl] = useState<HTMLDivElement | null>(null);
  const [availableContainerEl, setAvailableContainerEl] = useState<HTMLDivElement | null>(null);

  const availableSentinelRef = useCallback(
    (element: HTMLDivElement | null) => { setAvailableSentinelEl(element); },
    [],
  );
  const availableScrollContainerRef = useCallback(
    (element: HTMLDivElement | null) => { setAvailableContainerEl(element); },
    [],
  );

  // ── Assigned panel — infinite scroll ─────────────────────────────────
  const [assigned, setAssigned] = useState<GroupAdminItem[]>([]);
  const [isAssignedLoading, setIsAssignedLoading] = useState(false);
  const [hasMoreAssigned, setHasMoreAssigned] = useState(true);
  const [totalAssigned, setTotalAssigned] = useState(0);
  const assignedPageRef = useRef(1);
  const assignedAccumulatedRef = useRef(0);
  const latestAssignedRequestRef = useRef(0);
  const fetchAssignedRef = useRef<() => Promise<void>>(async () => {});
  const isFetchingAssignedRef = useRef(false);
  const [assignedSentinelEl, setAssignedSentinelEl] = useState<HTMLDivElement | null>(null);
  const [assignedContainerEl, setAssignedContainerEl] = useState<HTMLDivElement | null>(null);

  const assignedSentinelRef = useCallback(
    (element: HTMLDivElement | null) => { setAssignedSentinelEl(element); },
    [],
  );
  const assignedScrollContainerRef = useCallback(
    (element: HTMLDivElement | null) => { setAssignedContainerEl(element); },
    [],
  );

  // ── Search text ───────────────────────────────────────────────────────
  const [availableSearchText, setAvailableSearchText] = useState('');
  const [assignedSearchText, setAssignedSearchText] = useState('');
  const debouncedAvailableSearchText = useDebounce(availableSearchText, SEARCH_DEBOUNCE_MS);
  const debouncedAssignedSearchText = useDebounce(assignedSearchText, SEARCH_DEBOUNCE_MS);

  // ── Original state (for cancel) ───────────────────────────────────────
  const originalAssignedRef = useRef<GroupAdminItem[]>([]);
  const originalAvailableRef = useRef<GroupAdminItem[]>([]);
  const originalTotalAssignedRef = useRef(0);
  const originalTotalAvailableRef = useRef(0);
  const originalHasMoreAssignedRef = useRef(false);
  const originalHasMoreAvailableRef = useRef(false);
  const originalsStoredRef = useRef(false);
  const cancelAvailableResetRef = useRef(false);
  const cancelAssignedResetRef = useRef(false);

  // ── Selection state ───────────────────────────────────────────────────
  const [selectedAvailableIds, setSelectedAvailableIds] = useState<Set<number>>(new Set());
  const [selectedAssignedIds, setSelectedAssignedIds] = useState<Set<number>>(new Set());

  // ── Delta tracking (for save) ─────────────────────────────────────────
  const [addedIds, setAddedIds] = useState<Set<number>>(new Set());
  const [removedIds, setRemovedIds] = useState<Set<number>>(new Set());
  const [isAddAllPending, setIsAddAllPending] = useState(false);
  const [isRemoveAllPending, setIsRemoveAllPending] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const addedIdsRef = useRef<Set<number>>(new Set());
  const removedIdsRef = useRef<Set<number>>(new Set());
  useEffect(() => { addedIdsRef.current = addedIds; }, [addedIds]);
  useEffect(() => { removedIdsRef.current = removedIds; }, [removedIds]);

  const isAddAllPendingRef = useRef(false);
  const isRemoveAllPendingRef = useRef(false);
  useEffect(() => { isAddAllPendingRef.current = isAddAllPending; }, [isAddAllPending]);
  useEffect(() => { isRemoveAllPendingRef.current = isRemoveAllPending; }, [isRemoveAllPending]);

  // ── Fetch available ───────────────────────────────────────────────────
  const fetchAvailable = useCallback(async () => {
    if (!selectedGroupId) return;
    if (isFetchingAvailableRef.current) return;
    if (isRemoveAllPendingRef.current) return;

    isFetchingAvailableRef.current = true;
    setIsAvailableLoading(true);
    const requestId = ++latestAvailableRequestRef.current;
    const page = availablePageRef.current;

    const response = await groupAdminsAvailableService.getAvailable(
      selectedGroupId,
      {
        page,
        pageSize: PAGE_SIZE,
        ...(debouncedAvailableSearchText ? { searchText: debouncedAvailableSearchText } : {}),
      },
    );

    if (requestId !== latestAvailableRequestRef.current) {
      isFetchingAvailableRef.current = false;
      setIsAvailableLoading(false);
      return;
    }

    if (response.success && response.data) {
      const totalRecords = response.data.totalRecords;
      const mappedData = response.data.items.filter(
        (employee) => !addedIdsRef.current.has(employee.employeeId),
      );

      if (page === 1) {
        const adjustedTotal = totalRecords
          - addedIdsRef.current.size
          + removedIdsRef.current.size;
        setTotalAvailable(adjustedTotal);
        availableAccumulatedRef.current = mappedData.length;
        setAvailable(mappedData);
      } else {
        availableAccumulatedRef.current += mappedData.length;
        setAvailable((previous) => [...previous, ...mappedData]);
      }

      setHasMoreAvailable(
        mappedData.length > 0 && availableAccumulatedRef.current < totalRecords,
      );
    } else {
      showApiError(response, translate('securityGrouping:assignAdministrators.messages.loadError'));
    }

    isFetchingAvailableRef.current = false;
    setIsAvailableLoading(false);
  }, [selectedGroupId, debouncedAvailableSearchText, showApiError, translate]);

  useEffect(() => { fetchAvailableRef.current = fetchAvailable; }, [fetchAvailable]);

  useEffect(() => {
    if (cancelAvailableResetRef.current) return;
    if (isRemoveAllPending) return;
    availablePageRef.current = 1;
    availableAccumulatedRef.current = 0;
    isFetchingAvailableRef.current = false;
    setAvailable([]);
    setHasMoreAvailable(true);
  }, [debouncedAvailableSearchText, isRemoveAllPending]);

  useEffect(() => { void fetchAvailable(); }, [fetchAvailable]);

  useEffect(() => {
    if (!availableSentinelEl || !availableContainerEl ||
      !hasMoreAvailable || isRemoveAllPending) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isFetchingAvailableRef.current) {
          availablePageRef.current += 1;
          void fetchAvailableRef.current();
        }
      },
      { root: availableContainerEl, threshold: 0 },
    );

    observer.observe(availableSentinelEl);
    return () => observer.disconnect();
  }, [hasMoreAvailable, availableSentinelEl, availableContainerEl, isRemoveAllPending]);

  // ── Fetch assigned ────────────────────────────────────────────────────
  const fetchAssigned = useCallback(async () => {
    if (!selectedGroupId) return;
    if (isFetchingAssignedRef.current) return;
    if (isAddAllPendingRef.current) return;

    isFetchingAssignedRef.current = true;
    setIsAssignedLoading(true);
    const requestId = ++latestAssignedRequestRef.current;
    const page = assignedPageRef.current;

    const response = await groupAdminsAssignedService.getAssigned(
      selectedGroupId,
      {
        page,
        pageSize: PAGE_SIZE,
        ...(debouncedAssignedSearchText ? { searchText: debouncedAssignedSearchText } : {}),
      },
    );

    if (requestId !== latestAssignedRequestRef.current) {
      isFetchingAssignedRef.current = false;
      setIsAssignedLoading(false);
      return;
    }

    if (response.success && response.data) {
      const totalRecords = response.data.totalRecords;
      const mappedData = response.data.items.filter(
        (employee) => !removedIdsRef.current.has(employee.employeeId),
      );

      if (page === 1) {
        const adjustedTotal = totalRecords
          - removedIdsRef.current.size
          + addedIdsRef.current.size;
        setTotalAssigned(adjustedTotal);
        assignedAccumulatedRef.current = mappedData.length;
        setAssigned(mappedData);
      } else {
        assignedAccumulatedRef.current += mappedData.length;
        setAssigned((previous) => [...previous, ...mappedData]);
      }

      setHasMoreAssigned(
        mappedData.length > 0 && assignedAccumulatedRef.current < totalRecords,
      );
    } else {
      showApiError(response, translate('securityGrouping:assignAdministrators.messages.loadError'));
    }

    isFetchingAssignedRef.current = false;
    setIsAssignedLoading(false);
  }, [selectedGroupId, debouncedAssignedSearchText, showApiError, translate]);

  useEffect(() => { fetchAssignedRef.current = fetchAssigned; }, [fetchAssigned]);

  useEffect(() => {
    if (cancelAssignedResetRef.current) return;
    if (isAddAllPending) return;
    assignedPageRef.current = 1;
    assignedAccumulatedRef.current = 0;
    isFetchingAssignedRef.current = false;
    setAssigned([]);
    setHasMoreAssigned(true);
  }, [debouncedAssignedSearchText, isAddAllPending]);

  useEffect(() => { void fetchAssigned(); }, [fetchAssigned]);

  useEffect(() => {
    if (!assignedSentinelEl || !assignedContainerEl || !hasMoreAssigned || isAddAllPending) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isFetchingAssignedRef.current) {
          assignedPageRef.current += 1;
          void fetchAssignedRef.current();
        }
      },
      { root: assignedContainerEl, threshold: 0 },
    );

    observer.observe(assignedSentinelEl);
    return () => observer.disconnect();
  }, [hasMoreAssigned, assignedSentinelEl, assignedContainerEl, isAddAllPending]);

  // ── Group change — reset both panels ─────────────────────────────────
  const onGroupChange = useCallback((groupId: number | null) => {
    setSelectedGroupId(groupId);
    setAvailable([]);
    setAssigned([]);
    setSelectedAvailableIds(new Set());
    setSelectedAssignedIds(new Set());
    availablePageRef.current = 1;
    availableAccumulatedRef.current = 0;
    assignedPageRef.current = 1;
    assignedAccumulatedRef.current = 0;
    setHasMoreAvailable(true);
    setHasMoreAssigned(true);
    setTotalAvailable(0);
    setTotalAssigned(0);
    setAddedIds(new Set());
    setRemovedIds(new Set());
    setIsAddAllPending(false);
    setIsRemoveAllPending(false);
    originalAssignedRef.current = [];
    originalAvailableRef.current = [];
    originalsStoredRef.current = false;
    isFetchingAvailableRef.current = false;
    isFetchingAssignedRef.current = false;
    setAvailableSearchText('');
    setAssignedSearchText('');
    setIsDirty(false);
    if (groupId !== null) {
      setIsAvailableLoading(true);
      setIsAssignedLoading(true);
    }
  }, []);

  // Store originals once after both panels finish loading for the selected group
  useEffect(() => {
    if (!selectedGroupId) return;
    if (isAvailableLoading || isAssignedLoading) return;
    if (originalsStoredRef.current) return;
    originalsStoredRef.current = true;
    originalAssignedRef.current = assigned;
    originalAvailableRef.current = available;
    originalTotalAssignedRef.current = totalAssigned;
    originalTotalAvailableRef.current = totalAvailable;
    originalHasMoreAssignedRef.current = hasMoreAssigned;
    originalHasMoreAvailableRef.current = hasMoreAvailable;
  }, [
    isAvailableLoading, isAssignedLoading, assigned, available,
    selectedGroupId, totalAssigned, totalAvailable, hasMoreAssigned, hasMoreAvailable,
  ]);

  // ── Client-side search filtering ──────────────────────────────────────
  const filteredAvailable = useMemo(() => {
    const query = availableSearchText.toLowerCase().trim();
    const withoutMoved = available.filter((e) => !addedIds.has(e.employeeId));
    if (!query) return withoutMoved;
    return withoutMoved.filter(
      (employee) =>
        employee.employeeName.toLowerCase().includes(query) ||
        employee.employeeTaxIdentificationNumber.toLowerCase().includes(query),
    );
  }, [available, availableSearchText, addedIds]);

  const filteredAssigned = useMemo(() => {
    const query = assignedSearchText.toLowerCase().trim();
    const withoutMoved = assigned.filter((e) => !removedIds.has(e.employeeId));
    if (!query) return withoutMoved;
    return withoutMoved.filter(
      (employee) =>
        employee.employeeName.toLowerCase().includes(query) ||
        employee.employeeTaxIdentificationNumber.toLowerCase().includes(query),
    );
  }, [assigned, assignedSearchText, removedIds]);

  // ── Selection handlers — available ────────────────────────────────────
  const onToggleAvailable = useCallback((employeeId: number) => {
    setSelectedAvailableIds((previous) => {
      const next = new Set(previous);
      if (next.has(employeeId)) { next.delete(employeeId); } else { next.add(employeeId); }
      return next;
    });
  }, []);

  const isAllAvailableSelected = useMemo(
    () =>
      filteredAvailable.length > 0 &&
      filteredAvailable.every((e) => selectedAvailableIds.has(e.employeeId)),
    [filteredAvailable, selectedAvailableIds],
  );

  const isAvailableIndeterminate = useMemo(
    () =>
      !isAllAvailableSelected &&
      filteredAvailable.some((e) => selectedAvailableIds.has(e.employeeId)),
    [filteredAvailable, selectedAvailableIds, isAllAvailableSelected],
  );

  const onSelectAllAvailable = useCallback(() => {
    if (isAllAvailableSelected) {
      setSelectedAvailableIds((previous) => {
        const next = new Set(previous);
        filteredAvailable.forEach((e) => next.delete(e.employeeId));
        return next;
      });
    } else {
      setSelectedAvailableIds((previous) => {
        const next = new Set(previous);
        filteredAvailable.forEach((e) => next.add(e.employeeId));
        return next;
      });
    }
  }, [filteredAvailable, isAllAvailableSelected]);

  // ── Selection handlers — assigned ─────────────────────────────────────
  const onToggleAssigned = useCallback((employeeId: number) => {
    setSelectedAssignedIds((previous) => {
      const next = new Set(previous);
      if (next.has(employeeId)) { next.delete(employeeId); } else { next.add(employeeId); }
      return next;
    });
  }, []);

  const isAllAssignedSelected = useMemo(
    () =>
      filteredAssigned.length > 0 &&
      filteredAssigned.every((e) => selectedAssignedIds.has(e.employeeId)),
    [filteredAssigned, selectedAssignedIds],
  );

  const isAssignedIndeterminate = useMemo(
    () =>
      !isAllAssignedSelected &&
      filteredAssigned.some((e) => selectedAssignedIds.has(e.employeeId)),
    [filteredAssigned, selectedAssignedIds, isAllAssignedSelected],
  );

  const onSelectAllAssigned = useCallback(() => {
    if (isAllAssignedSelected) {
      setSelectedAssignedIds((previous) => {
        const next = new Set(previous);
        filteredAssigned.forEach((e) => next.delete(e.employeeId));
        return next;
      });
    } else {
      setSelectedAssignedIds((previous) => {
        const next = new Set(previous);
        filteredAssigned.forEach((e) => next.add(e.employeeId));
        return next;
      });
    }
  }, [filteredAssigned, isAllAssignedSelected]);

  // ── Move operations ───────────────────────────────────────────────────
  const onAdd = useCallback(() => {
    const movedCount = selectedAvailableIds.size;
    setAddedIds((previous) => {
      const next = new Set(previous);
      selectedAvailableIds.forEach((id) => next.add(id));
      return next;
    });
    setRemovedIds((previous) => {
      const next = new Set(previous);
      selectedAvailableIds.forEach((id) => next.delete(id));
      return next;
    });
    setAvailable((previous) => previous.filter((e) => !selectedAvailableIds.has(e.employeeId)));
    setAssigned((previous) => [
      ...previous,
      ...available.filter((e) => selectedAvailableIds.has(e.employeeId)),
    ]);
    setTotalAvailable((previous) => previous - movedCount);
    setTotalAssigned((previous) => previous + movedCount);
    setSelectedAvailableIds(new Set());
    setIsDirty(true);
  }, [available, selectedAvailableIds]);

  const onRemove = useCallback(() => {
    const movedCount = selectedAssignedIds.size;
    setRemovedIds((previous) => {
      const next = new Set(previous);
      selectedAssignedIds.forEach((id) => next.add(id));
      return next;
    });
    setAddedIds((previous) => {
      const next = new Set(previous);
      selectedAssignedIds.forEach((id) => next.delete(id));
      return next;
    });
    setAssigned((previous) => previous.filter((e) => !selectedAssignedIds.has(e.employeeId)));
    setAvailable((previous) => [
      ...previous,
      ...assigned.filter((e) => selectedAssignedIds.has(e.employeeId)),
    ]);
    setTotalAssigned((previous) => previous - movedCount);
    setTotalAvailable((previous) => previous + movedCount);
    setSelectedAssignedIds(new Set());
    setIsDirty(true);
  }, [assigned, selectedAssignedIds]);

  const onDoubleClickAvailable = useCallback((employee: GroupAdminItem) => {
    setAddedIds((previous) => {
      const next = new Set(previous); next.add(employee.employeeId); return next;
    });
    setRemovedIds((previous) => {
      const next = new Set(previous); next.delete(employee.employeeId); return next;
    });
    setAvailable((previous) => previous.filter((e) => e.employeeId !== employee.employeeId));
    setAssigned((previous) => [...previous, employee]);
    setTotalAvailable((previous) => previous - 1);
    setTotalAssigned((previous) => previous + 1);
    setSelectedAvailableIds((previous) => {
      const next = new Set(previous); next.delete(employee.employeeId); return next;
    });
    setIsDirty(true);
  }, []);

  const onDoubleClickAssigned = useCallback((employee: GroupAdminItem) => {
    setRemovedIds((previous) => {
      const next = new Set(previous); next.add(employee.employeeId); return next;
    });
    setAddedIds((previous) => {
      const next = new Set(previous); next.delete(employee.employeeId); return next;
    });
    setAssigned((previous) => previous.filter((e) => e.employeeId !== employee.employeeId));
    setAvailable((previous) => [...previous, employee]);
    setTotalAssigned((previous) => previous - 1);
    setTotalAvailable((previous) => previous + 1);
    setSelectedAssignedIds((previous) => {
      const next = new Set(previous); next.delete(employee.employeeId); return next;
    });
    setIsDirty(true);
  }, []);

  // ── Server-wide bulk operations ───────────────────────────────────────
  const onAddAllFromServer = useCallback(() => {
    setIsAddAllPending(true);
    setIsRemoveAllPending(false);
    setAddedIds(new Set());
    setRemovedIds(new Set());
    setAssigned((previous) => [...previous, ...available]);
    setAvailable([]);
    setTotalAssigned((previous) => previous + totalAvailable);
    setTotalAvailable(0);
    setHasMoreAvailable(false);
    setHasMoreAssigned(available.length < totalAvailable);
    availablePageRef.current = 1;
    availableAccumulatedRef.current = 0;
    setSelectedAvailableIds(new Set());
    setIsDirty(true);
  }, [available, totalAvailable]);

  const onRemoveAllFromServer = useCallback(() => {
    setIsRemoveAllPending(true);
    setIsAddAllPending(false);
    setAddedIds(new Set());
    setRemovedIds(new Set());
    setAvailable((previous) => [...previous, ...assigned]);
    setAssigned([]);
    setTotalAvailable((previous) => previous + totalAssigned);
    setTotalAssigned(0);
    setHasMoreAssigned(false);
    setHasMoreAvailable(assigned.length < totalAssigned);
    assignedPageRef.current = 1;
    assignedAccumulatedRef.current = 0;
    setSelectedAssignedIds(new Set());
    setIsDirty(true);
  }, [assigned, totalAssigned]);

  // ── Search handlers ───────────────────────────────────────────────────
  const onAvailableSearch = useCallback((text: string) => { setAvailableSearchText(text); }, []);
  const onAssignedSearch = useCallback((text: string) => { setAssignedSearchText(text); }, []);

  // ── Save ──────────────────────────────────────────────────────────────
  const [isSaving, setIsSaving] = useState(false);

  const onSave = useCallback(async () => {
    if (!selectedGroupId) return;
    setIsSaving(true);

    let body: LC_SaveGroupAdminsIn;
    if (isAddAllPending) {
      body = { addAll: true, employeeIdsToRemove: removedIds.size > 0 ? [...removedIds] : null };
    } else if (isRemoveAllPending) {
      body = { removeAll: true, employeeIdsToAdd: addedIds.size > 0 ? [...addedIds] : null };
    } else {
      body = {
        employeeIdsToAdd: addedIds.size > 0 ? [...addedIds] : null,
        employeeIdsToRemove: removedIds.size > 0 ? [...removedIds] : null,
      };
    }

    const response = await groupAdminsSaveService.save(selectedGroupId, body);
    if (response.success) {
      showNotification('success', translate('securityGrouping:assignAdministrators.messages.saveSuccess'));

      setAvailable([]);
      setAssigned([]);
      setSelectedAvailableIds(new Set());
      setSelectedAssignedIds(new Set());
      setAddedIds(new Set());
      setRemovedIds(new Set());
      setIsAddAllPending(false);
      setIsRemoveAllPending(false);
      availablePageRef.current = 1;
      availableAccumulatedRef.current = 0;
      assignedPageRef.current = 1;
      assignedAccumulatedRef.current = 0;
      setHasMoreAvailable(true);
      setHasMoreAssigned(true);
      isFetchingAvailableRef.current = false;
      isFetchingAssignedRef.current = false;
      originalsStoredRef.current = false;
      isAddAllPendingRef.current = false;
      isRemoveAllPendingRef.current = false;
      setAvailableSearchText('');
      setAssignedSearchText('');
      setIsDirty(false);

      await Promise.all([fetchAvailableRef.current(), fetchAssignedRef.current()]);
    } else {
      showApiError(response, translate('securityGrouping:assignAdministrators.messages.saveError'));
    }

    setIsSaving(false);
  }, [
    selectedGroupId, isAddAllPending, isRemoveAllPending,
    addedIds, removedIds, showApiError, showNotification, translate,
  ]);

  // ── Cancel ────────────────────────────────────────────────────────────
  const onCancel = useCallback(() => {
    cancelAvailableResetRef.current = true;
    cancelAssignedResetRef.current = true;
    setTimeout(() => {
      cancelAvailableResetRef.current = false;
      cancelAssignedResetRef.current = false;
    }, CANCEL_GUARD_DELAY_MS);
    setAssigned(originalAssignedRef.current);
    setAvailable(originalAvailableRef.current);
    setTotalAssigned(originalTotalAssignedRef.current);
    setTotalAvailable(originalTotalAvailableRef.current);
    setHasMoreAssigned(originalHasMoreAssignedRef.current);
    setHasMoreAvailable(originalHasMoreAvailableRef.current);
    availablePageRef.current = Math.ceil(originalAvailableRef.current.length / PAGE_SIZE) || 1;
    availableAccumulatedRef.current = originalAvailableRef.current.length;
    assignedPageRef.current = Math.ceil(originalAssignedRef.current.length / PAGE_SIZE) || 1;
    assignedAccumulatedRef.current = originalAssignedRef.current.length;
    setSelectedAvailableIds(new Set());
    setSelectedAssignedIds(new Set());
    setAddedIds(new Set());
    setRemovedIds(new Set());
    setIsAddAllPending(false);
    setIsRemoveAllPending(false);
    setAvailableSearchText('');
    setAssignedSearchText('');
    setIsDirty(false);
  }, []);

  return {
    groupingOptions,
    selectedGroupId,
    onGroupChange,
    available,
    filteredAvailable,
    isAvailableLoading,
    hasMoreAvailable,
    totalAvailable,
    availableSearchText,
    onAvailableSearch,
    availableSentinelRef,
    availableScrollContainerRef,
    assigned,
    filteredAssigned,
    isAssignedLoading,
    hasMoreAssigned,
    totalAssigned,
    assignedSearchText,
    onAssignedSearch,
    assignedSentinelRef,
    assignedScrollContainerRef,
    selectedAvailableIds,
    onToggleAvailable,
    onSelectAllAvailable,
    isAllAvailableSelected,
    isAvailableIndeterminate,
    selectedAssignedIds,
    onToggleAssigned,
    onSelectAllAssigned,
    isAllAssignedSelected,
    isAssignedIndeterminate,
    onAdd,
    onRemove,
    onDoubleClickAvailable,
    onDoubleClickAssigned,
    onAddAllFromServer,
    onRemoveAllFromServer,
    isAddAllPending,
    isRemoveAllPending,
    isDirty,
    isSaving,
    canEdit,
    onSave,
    onCancel,
  };
}
